import type { InventoryAdjustmentReasons } from '@/domains/adjustment-groups/types/consts/AdjustmentReason.consts'
import type { StockSaveRequest } from '@/domains/command-center/composables/use-stock-mode/useStockAdjustmentSave.types'
import type {
  StockHeldScan,
  StockItemStepInput,
  StockLocation,
  StockMenuEntry,
  StockMenuItem,
  StockQuantityStepInput,
  StockSavedLine,
  StockShelf,
  StockVariant,
  StockVariantStepInput
} from '@/domains/command-center/handlers/stock/stockMode.types'
import type { MeasurementUnit } from '@/domains/measurement-units/types/models/MeasurementUnit.types'
import type { IndexProduct } from '@/domains/products/types/models/IndexProduct.types'
import type { AdjustmentMode } from '@/domains/quick-updates/types/models/QuickInventoryUpdate.types'

import { ADJUSTMENT_MODES, REASONS_BY_MODE } from '@/domains/adjustment-groups/types/consts/AdjustmentGroupMode.consts'
import { CONSIGNMENT_ONLY_REASONS } from '@/domains/adjustment-groups/types/requests/CreateInventoryAdjustmentRequest.types'
import { stockOutcome } from '@/domains/adjustment-groups/utils/stock-outcome/stockOutcome'
import {
  MODE_ICONS,
  MODE_VERBS,
  REASON_HEADINGS,
  ROW_ICONS,
  STOCK_ERROR_ROW_ID
} from '@/domains/command-center/handlers/stock/stockMode.consts'
import {
  shelfAt,
  stockUnitOfStockVariant,
  stockVariantName,
  stockVariantsOf
} from '@/domains/command-center/handlers/stock/stockVariants'
import { MIN_SEARCH_LENGTH } from '@/domains/command-center/services/searchService'
import { baseUnitOfRelation } from '@/domains/measurement-units/composables/use-measurement-units-query/useMeasurementUnitsQuery.utils'
import { notWholeMessage } from '@/domains/measurement-units/utils/stock-quantity-input/stockQuantityInput'
import { toTitleCase } from '@/utils/casing-util/casingUtil'
import { formatStock } from '@/utils/unit-util/unitUtil'

/** A refused save, as its row and the alert both say it. */
const notSavedLine = (message: string): string => `Not saved: ${message}`

/** A line of text in the list, drawn muted and never picked. */
const statusRow = (id: string, label: string): StockMenuItem => ({ id, label, disabled: true, isStatus: true })

const reasonLabel = (reason: InventoryAdjustmentReasons): string => toTitleCase(reason)

/** The reason as a labelled line, so a bare word after the location cannot be read as anything else. */
const reasonLine = (reason: InventoryAdjustmentReasons | null): string =>
  `Reason: ${reason ? reasonLabel(reason) : 'not chosen yet'}`

/**
 * The reasons the palette offers for a mode: the Quick Update page's list, less consignment.
 *
 * A consignment change must say whose goods moved, and the palette asks for no supplier.
 */
const paletteReasonsFor = (mode: AdjustmentMode): InventoryAdjustmentReasons[] =>
  REASONS_BY_MODE[mode].filter(reason => !CONSIGNMENT_ONLY_REASONS.includes(reason))

// ── Item step ────────────────────────────────────────────────────────────────────────────────────

/** Why a variant cannot be chosen here, or null when it can. */
const variantRefusal = (
  variant: StockVariant,
  shelf: StockShelf,
  location: StockLocation,
  canStartTracking: boolean
): string | null => {
  if (!variant.isTracking) return 'Does not track stock'
  if (!shelf.item && !canStartTracking)
    return `Not stocked at ${location.name}. Your role cannot start tracking it here`

  return null
}

const variantRow = (
  variant: StockVariant,
  location: StockLocation,
  canStartTracking: boolean,
  label: string
): StockMenuItem => {
  const shelf = shelfAt(variant, location)
  const refusal = variantRefusal(variant, shelf, location, canStartTracking)

  return {
    id: `stock-variant-${variant.id}`,
    label,
    subLabel: refusal ?? undefined,
    icon: ROW_ICONS.item,
    disabled: refusal !== null,
    stock: { kind: 'variant', variant, shelf }
  }
}

/** A product with one variant is that variant. A product with several opens the variant step. */
const productRows = (product: IndexProduct, location: StockLocation, canStartTracking: boolean): StockMenuItem[] => {
  const variants = stockVariantsOf(product)

  if (variants.length === 1) {
    return [variantRow(variants[0], location, canStartTracking, product.title)]
  }

  return [
    {
      id: `stock-product-${product.id}`,
      label: product.title,
      subLabel: `${variants.length} variants`,
      icon: ROW_ICONS.item,
      disabled: variants.length === 0,
      stock: { kind: 'product', product }
    }
  ]
}

/** No location yet, so the merchant picks the shelf first. Offered in any step that needs one. */
const chooseLocationRow: StockMenuItem = {
  id: 'stock-choose-location',
  label: 'Choose a location',
  subLabel: 'Stock changes at one location at a time',
  icon: ROW_ICONS.location,
  stock: { kind: 'openLocations' }
}

/** Shown in place of the location prompt, or the list, while there is nothing yet to choose from. */
const loadingLocationsRow = statusRow('stock-locations-loading', 'Loading locations…')

const locationPrompt = (isLoadingLocations: boolean): StockMenuItem =>
  isLoadingLocations ? loadingLocationsRow : chooseLocationRow

/**
 * A step that needs a location before it can show its rows. The item keeps its heading, so the merchant still
 * sees what they are counting while they choose where.
 */
const locationNeededMenu = (itemName: string, isLoadingLocations: boolean): StockMenuEntry[] => [
  { id: 'stock-location-needed', label: itemName, items: [locationPrompt(isLoadingLocations)] }
]

/** The last save. A link to its history for a user who may open it, and a plain line for one who may not. */
const savedRow = (saved: StockSavedLine, canOpenHistory: boolean): StockMenuItem => {
  // A result, not a history entry: a green check, and the shelf before and after in the trailing figure.
  const line: StockMenuItem = {
    id: 'stock-saved',
    label: `Saved: ${stockVariantName(saved.variant)}`,
    icon: ROW_ICONS.saved,
    iconTone: 'success',
    stock: { kind: 'saved', saved }
  }

  if (!canOpenHistory) {
    return { ...line, subLabel: `${saved.location.name} · ${reasonLine(saved.reason)}`, disabled: true }
  }

  return {
    ...line,
    subLabel: `${saved.location.name} · ${reasonLine(saved.reason)} · Open Movement History`,
    // It opens another screen, so the palette closes behind it.
    onSuccess: 'closeMenu'
  }
}

const itemStepMenu = (input: StockItemStepInput): StockMenuEntry[] => {
  const output: StockMenuEntry[] = []

  if (input.saved) {
    output.push({ id: 'stock-saved-group', label: 'Just saved', items: [savedRow(input.saved, input.canOpenHistory)] })
  }

  if (!input.location) return [...output, locationPrompt(input.isLoadingLocations)]

  if (input.isLookingUp) return [statusRow('stock-looking-up', 'Looking up the barcode…'), ...output]

  if (input.query.length < MIN_SEARCH_LENGTH) {
    return [...output, statusRow('stock-hint', 'Scan a barcode, or type a product name')]
  }

  if (input.isSearching) return [statusRow('stock-searching', 'Searching…'), ...output]

  if (!input.products.length) return [...output, statusRow('stock-no-match', `No product matches "${input.query}"`)]

  const location = input.location

  return [
    ...output,
    {
      id: 'stock-products',
      label: 'Products',
      items: input.products.flatMap(product => productRows(product, location, input.canStartTracking))
    }
  ]
}

// ── Variant step ─────────────────────────────────────────────────────────────────────────────────

const variantStepMenu = (input: StockVariantStepInput): StockMenuEntry[] => [
  {
    id: 'stock-variants',
    label: input.product.title,
    items: input.variants.map(variant =>
      variantRow(variant, input.location, input.canStartTracking, variant.options || variant.productTitle)
    )
  }
]

// ── Quantity step ────────────────────────────────────────────────────────────────────────────────

/** A base count read in the variant's stock unit, as every stock figure in the palette reads. */
const stockFigure = (baseCount: number, variant: StockVariant, units?: MeasurementUnit[]): string =>
  formatStock({ baseCount, unit: stockUnitOfStockVariant(variant, units) })

/**
 * Why a mode cannot be saved for this number, or null when it can.
 *
 * ⚠ **A Set that matches the shelf on screen is still offered.** The save reads the shelf again before it
 * writes, so a count equal to a figure read minutes ago still corrects a shelf a sale has moved since.
 */
const outcomeRefusal = (mode: AdjustmentMode, quantity: number, input: StockQuantityStepInput): string | null => {
  const outcome = stockOutcome({ current: input.shelf.current, mode, quantity })

  if (outcome.isRefused)
    return `Only ${stockFigure(input.shelf.current, input.variant, input.units)} at ${input.location.name}`
  if (outcome.isUnchanged && mode !== 'set') return 'Type a number above zero'

  return null
}

const outcomeSubLabel = (mode: AdjustmentMode, input: StockQuantityStepInput): string =>
  `${input.location.name} · ${reasonLine(input.reasonFor(mode))}`

/**
 * The change on its way, in place of the rows, so the merchant sees what is being saved until it lands.
 *
 * ⚠ **Built from the request, never from the screen.** The location chip stays live during a save, and the row
 * must name the shelf the request writes to.
 */
const savingRow = (request: StockSaveRequest, units?: MeasurementUnit[]): StockMenuItem => {
  const current = shelfAt(request.variant, request.location).current

  return {
    id: 'stock-saving',
    label: `Saving: ${MODE_VERBS[request.mode]} ${stockFigure(request.quantity, request.variant, units)}…`,
    subLabel: `${request.location.name} · ${reasonLine(request.reason)}`,
    icon: ROW_ICONS.saving,
    stock: {
      kind: 'saving',
      variant: request.variant,
      mode: request.mode,
      outcome: stockOutcome({ current, mode: request.mode, quantity: request.quantity }),
      current
    }
  }
}

const outcomeRow = (mode: AdjustmentMode, quantity: number, input: StockQuantityStepInput): StockMenuItem => {
  const refusal = outcomeRefusal(mode, quantity, input)

  return {
    id: `stock-outcome-${mode}`,
    label: `${MODE_VERBS[mode]} ${stockFigure(quantity, input.variant, input.units)}`,
    subLabel: refusal ?? outcomeSubLabel(mode, input),
    icon: MODE_ICONS[mode],
    disabled: refusal !== null,
    stock: {
      kind: 'outcome',
      variant: input.variant,
      mode,
      outcome: stockOutcome({ current: input.shelf.current, mode, quantity }),
      current: input.shelf.current
    }
  }
}

/** The way into the reason step. It names the reason now set, so the merchant sees it beside the control that changes it. */
const changeReasonRow = (mode: AdjustmentMode, input: StockQuantityStepInput): StockMenuItem => {
  const reason = input.reasonFor(mode)

  return {
    id: 'stock-change-reason',
    label: 'Change the reason',
    subLabel: reason ? `Now: ${reasonLabel(reason)}` : 'Pick one to save',
    icon: ROW_ICONS.reason,
    stock: { kind: 'changeReason', mode }
  }
}

/** A figure finer than the smallest unit the shelf counts in, such as 4.8205 kg of grams. */
const notWholeLine = (input: StockQuantityStepInput): string => {
  return notWholeMessage(baseUnitOfRelation(input.units ?? [], input.variant.pricingUnit))
}

/** The one line that says what is wrong with the box, or null when nothing is. */
const quantityProblem = (input: StockQuantityStepInput): string | null => {
  if (input.input.isNotWhole) return notWholeLine(input)
  if (input.input.isBarcodeLike) return 'That looks like a barcode, not a quantity'
  if (input.input.isMalformed) return 'Use digits only. Type +12 to add, -3 to remove or =48 to set'
  if (input.input.quantity === null) return 'Type +12 to add, -3 to remove or =48 to set'

  return null
}

/** The heading over the rows: what the shelf holds now, or that it holds nothing yet. */
const shelfHeading = (input: StockQuantityStepInput): string =>
  input.shelf.item
    ? `${stockVariantName(input.variant)} · ${stockFigure(input.shelf.current, input.variant, input.units)} at ${input.location.name}`
    : `${stockVariantName(input.variant)} · Not stocked at ${input.location.name} yet`

/**
 * A held scan is a line above the rows, never in place of them, so the item on screen can still be saved.
 * The held item opens once it is.
 */
const heldLine = (held: StockHeldScan): string =>
  held.variant
    ? `Next: ${stockVariantName(held.variant)}. It opens once this one is saved.`
    : `${held.code} is waiting. Save this one, then scan it again.`

/**
 * The outcomes of the number typed, one row per mode, or the one line that says what is wrong with the box.
 *
 * ⚠ **A sign shows its own row and no other.** `=48` lists only "Set to 48", so Enter with nothing
 * highlighted has one thing to save. A plain number lists all three, and the merchant moves to one.
 * The palette never highlights a row itself, so a scanner's Enter has nothing to save.
 */
const quantityRows = (input: StockQuantityStepInput): StockMenuItem[] => {
  if (input.saving) return [savingRow(input.saving, input.units)]

  // ⚠ A scan, a held item and a product with one variant all arrive here without passing a variant row,
  // so the refusal those rows carry is applied again.
  const refusal = variantRefusal(input.variant, input.shelf, input.location, input.canStartTracking)
  if (refusal) return [statusRow('stock-variant-refused', refusal)]

  const problem = quantityProblem(input)
  const quantity = input.input.quantity

  if (problem || quantity === null) return [statusRow('stock-quantity-problem', problem ?? '')]

  const modes = input.input.mode ? [input.input.mode] : ADJUSTMENT_MODES
  const rows: StockMenuItem[] = modes.map(mode => outcomeRow(mode, quantity, input))

  if (input.input.mode) rows.push(changeReasonRow(input.input.mode, input))

  return rows
}

/** The item and its shelf head the step from the moment it opens, so the merchant sees what they count before typing. */
const quantityStepMenu = (input: StockQuantityStepInput): StockMenuEntry[] => {
  const output: StockMenuEntry[] = []

  if (input.error) output.push(statusRow(STOCK_ERROR_ROW_ID, notSavedLine(input.error)))
  if (input.held) output.push(statusRow('stock-held', heldLine(input.held)))

  return [...output, { id: 'stock-outcomes', label: shelfHeading(input), items: quantityRows(input) }]
}

// ── Reason and location steps ────────────────────────────────────────────────────────────────────

const reasonStepMenu = (mode: AdjustmentMode, selected: InventoryAdjustmentReasons | null): StockMenuEntry[] => [
  {
    id: 'stock-reasons',
    label: REASON_HEADINGS[mode],
    items: paletteReasonsFor(mode).map(reason => ({
      id: `stock-reason-${reason}`,
      label: reasonLabel(reason),
      subLabel: reason === selected ? 'Chosen' : undefined,
      icon: reason === selected ? ROW_ICONS.chosen : ROW_ICONS.reason,
      stock: { kind: 'reason', reason }
    }))
  },
  statusRow('stock-consignment-hint', 'A consignment change needs a supplier. Use the stock drawer on the product.')
]

const locationStepMenu = (
  locations: StockLocation[],
  selected: StockLocation | null,
  isLoadingLocations: boolean
): StockMenuEntry[] => {
  if (isLoadingLocations) return [loadingLocationsRow]

  return [
    {
      id: 'stock-locations',
      label: 'Locations',
      items: locations.map(location => ({
        id: `stock-location-${location.ulid}`,
        label: location.name,
        subLabel: location.ulid === selected?.ulid ? 'Chosen' : undefined,
        icon: location.ulid === selected?.ulid ? ROW_ICONS.chosen : ROW_ICONS.location,
        stock: { kind: 'location', location }
      }))
    }
  ]
}

export {
  itemStepMenu,
  locationNeededMenu,
  locationStepMenu,
  notSavedLine,
  paletteReasonsFor,
  quantityStepMenu,
  reasonStepMenu,
  variantStepMenu
}
